import { useState, useRef, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from "../../assets/images/logo.png";
import { HugeiconsIcon } from '@hugeicons/react';
import { Menu01Icon, Cancel01Icon, ShieldUserIcon, UserCircleIcon, Logout01Icon, ArrowDown01Icon, CheckmarkCircle01Icon, LayoutLeftIcon, Notification01Icon, Settings01Icon } from '@hugeicons/core-free-icons';
import { navigation } from "../Navigation/navigation";
import { selectStaff, selectAdminRole, selectPermissions, selectRoleSlugs, selectAccessToken, logout, setAdminRole } from "../../slices/staffSlice";
import { show } from '../../slices/toastSlice';
import { staffRoles, authStaff } from '../../utils/constants';
import { fetchRequests } from '../../Requests/getRequests';

function LiveClock() {
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    const datePart = now.toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' });
    const timePart = now.toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    return (
        <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:flex items-center gap-1.5 tabular-nums">
            {datePart}
            <span className="text-gray-400 dark:text-gray-600">·</span>
            <span className="text-gray-600 dark:text-gray-300 font-medium">{timePart}</span>
        </span>
    );
}

function RoleSwitcher({ roleSlugs, selectedRole }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const switchRole = (role) => {
        setOpen(false);
        dispatch(setAdminRole(role));
        dispatch(show({ type: "success", state: true, message: `Role Switched to ${staffRoles(role)}` }));
        setTimeout(() => navigate("/"), 300);
    };

    if (!roleSlugs || roleSlugs.length < 2) return null;

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 transition-colors min-w-[14.5rem]"
            >
                <span>{staffRoles(selectedRole)}</span>
                <HugeiconsIcon icon={ArrowDown01Icon} size={14} className={`text-gray-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute left-0 top-full mt-1.5 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-1.5 z-50 min-w-[14.5rem] overflow-hidden">
                    <p className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Switch Role</p>
                    {roleSlugs.map((role, i) => (
                        <button
                            key={i}
                            onClick={() => switchRole(role)}
                            className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm transition-colors ${
                                role === selectedRole
                                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                            <span>{staffRoles(role)}</span>
                            {role === selectedRole && (
                                <HugeiconsIcon icon={CheckmarkCircle01Icon} size={15} className="text-blue-500 shrink-0" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function timeAgo(dateStr) {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

function NotificationBell({ accessToken }) {
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const ref = useRef(null);

    const fetchNotifications = useCallback(async () => {
        const res = await fetchRequests(`/auth/staff/notifications`, "GET", authStaff(accessToken), {}, {});
        if (Array.isArray(res)) setNotifications(res);
    }, [accessToken]);

    useEffect(() => {
        if (!accessToken) return;
        fetchNotifications();
        const id = setInterval(fetchNotifications, 60000);
        return () => clearInterval(id);
    }, [accessToken, fetchNotifications]);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const unreadCount = notifications.filter(n => !n.read).length;

    const markAllRead = async () => {
        await fetchRequests(`/auth/staff/notifications/read-all`, "PATCH", authStaff(accessToken), {}, {});
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const markOneRead = async (id) => {
        await fetchRequests(`/auth/staff/notifications/${id}/read`, "PATCH", authStaff(accessToken), {}, {});
        setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    };

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                className="relative p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Notifications"
            >
                <HugeiconsIcon icon={Notification01Icon} size={20} />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">Notifications</p>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllRead}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 font-medium transition-colors"
                            >
                                Mark all read
                            </button>
                        )}
                    </div>

                    <div className="overflow-y-auto max-h-80 divide-y divide-gray-50 dark:divide-gray-700">
                        {notifications.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-8">No notifications</p>
                        ) : (
                            notifications.map(n => (
                                <button
                                    key={n._id}
                                    onClick={() => markOneRead(n._id)}
                                    className={`w-full text-left px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${!n.read ? 'bg-blue-50/40 dark:bg-blue-500/10' : ''}`}
                                >
                                    <div className="flex items-start gap-2.5">
                                        {!n.read && (
                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                                        )}
                                        <div className={`flex-1 min-w-0 ${n.read ? 'pl-4' : ''}`}>
                                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 leading-snug">{n.title}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug line-clamp-2">{n.body}</p>
                                            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{timeAgo(n.createdAt)}</p>
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function MobileRoleSwitcher({ roleSlugs, selectedRole, onSwitch }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
                <span>{staffRoles(selectedRole)}</span>
                <HugeiconsIcon icon={ArrowDown01Icon} size={14} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute left-0 right-0 top-full mt-1.5 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-1.5 z-10 overflow-hidden">
                    <p className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Switch Role</p>
                    {roleSlugs.map((role, i) => (
                        <button
                            key={i}
                            onClick={() => { setOpen(false); onSwitch(role); }}
                            className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm transition-colors ${
                                role === selectedRole
                                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                            <span>{staffRoles(role)}</span>
                            {role === selectedRole && (
                                <HugeiconsIcon icon={CheckmarkCircle01Icon} size={15} className="text-blue-500 shrink-0" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function Topbar() {
    const dispatch = useDispatch();
    const location = useLocation();
    const { pathname } = location;
    const path = pathname.split("/");

    const navigate = useNavigate();
    const staff = useSelector(selectStaff);
    const selectedRole = useSelector(selectAdminRole);
    const permissions = useSelector(selectPermissions);
    const roleSlugs = useSelector(selectRoleSlugs);
    const accessToken = useSelector(selectAccessToken);
    const navItems = navigation();

    const hasPermission = (permission) =>
        !permission || permissions.includes('*:*') || permissions.includes(permission);

    const rawLinks = selectedRole === "loan_officer" ? navItems?.loLinks : navItems?.links;
    const staffLinks = rawLinks?.filter(item => hasPermission(item.permission));

    const [mobileOpen, setMobileOpen] = useState(false);
    const [userOpen, setUserOpen] = useState(false);
    const userRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const closeMobileNav = () => {
        setMobileOpen(false);
        const sidebar = document.getElementById("sidebar-mobile");
        if (sidebar) sidebar.classList.add("hide-mobile-menu");
    };

    const toggleMobileNav = () => {
        const sidebar = document.getElementById("sidebar-mobile");
        if (!sidebar) return;
        if (sidebar.classList.contains("hidden") || sidebar.classList.contains("hide-mobile-menu")) {
            sidebar.classList.remove("hidden");
            sidebar.classList.remove("hide-mobile-menu");
            setMobileOpen(true);
        } else {
            closeMobileNav();
        }
    };

    const logoutRequest = () => dispatch(logout());

    const initials = staff
        ? ((staff.otherNames?.[0] ?? '') + (staff.surname?.[0] ?? '')).toUpperCase()
        : '?';

    return (
        <>
        {/* ── Desktop header ── */}
        <header className="hidden md:flex items-center gap-4 px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shrink-0 relative z-10 topbar-desktop">

            {/* Sidebar collapse toggle */}
            <button
                onClick={() => window.dispatchEvent(new CustomEvent('toggle-sidebar'))}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
                title="Toggle sidebar"
            >
                <HugeiconsIcon icon={LayoutLeftIcon} size={20} />
            </button>

            {/* Role switcher — only on staff portal (not admin) */}
            {path[1] !== "admin" && (
                <RoleSwitcher roleSlugs={roleSlugs} selectedRole={selectedRole} />
            )}

            {/* Push clock and user to the right */}
            <div className="flex items-center gap-4 ml-auto">
                <LiveClock />
                <NotificationBell accessToken={accessToken} />

                {/* User dropdown */}
                <div ref={userRef} className="relative">
                    <button
                        onClick={() => setUserOpen(o => !o)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#1e3a6e' }}
                        aria-label="User menu"
                    >
                        {initials}
                    </button>

                    {userOpen && (
                        <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50">
                            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">
                                    {staff?.otherNames} {staff?.surname}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{staff?.email}</p>
                            </div>

                            <div className="py-1">
                                {hasPermission('admin:access') && (
                                    <Link
                                        to={path[1] === "admin" ? "/" : "/admin/home"}
                                        onClick={() => setUserOpen(false)}
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                                    >
                                        <HugeiconsIcon icon={ShieldUserIcon} size={16} className="text-gray-400 dark:text-gray-500" />
                                        {path[1] === "admin" ? "Staff Portal" : "Admin Portal"}
                                    </Link>
                                )}
                                <Link
                                    to={path[1] === "admin" ? "/admin/profile" : "/profile"}
                                    onClick={() => setUserOpen(false)}
                                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                                >
                                    <HugeiconsIcon icon={UserCircleIcon} size={16} className="text-gray-400 dark:text-gray-500" />
                                    Profile
                                </Link>
                                <Link
                                    to="/settings"
                                    onClick={() => setUserOpen(false)}
                                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                                >
                                    <HugeiconsIcon icon={Settings01Icon} size={16} className="text-gray-400 dark:text-gray-500" />
                                    Settings
                                </Link>
                            </div>

                            <div className="border-t border-gray-100 dark:border-gray-700 py-1">
                                <button
                                    onClick={logoutRequest}
                                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                >
                                    <HugeiconsIcon icon={Logout01Icon} size={16} className="text-red-500 dark:text-red-400" />
                                    Sign out
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>

        {/* ── Mobile topbar ── */}
        <div className='w-full shadow-sm py-2 px-2 md:hidden bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800'>
            <div className='section-container p-2 flex items-center justify-between z-10'>
                <Link to={path[1] === "admin" ? `/admin/home` : `/`}>
                    <img src={Logo} className='w-auto h-12' alt='CedisPay Micro-Finance Limited' loading='lazy' />
                </Link>

                <button
                    className='p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
                    onClick={toggleMobileNav}
                >
                    <HugeiconsIcon icon={mobileOpen ? Cancel01Icon : Menu01Icon} size={24} />
                </button>
            </div>
        </div>

        {/* ── Mobile nav backdrop ── */}
        {mobileOpen && (
            <div
                className="fixed inset-0 bg-black/50 z-30 md:hidden"
                onClick={closeMobileNav}
                aria-hidden="true"
            />
        )}

        {/* ── Mobile nav drawer ── */}
        <aside id="sidebar-mobile" aria-label='Sidebar Mobile' className='mobile-menu hidden lg:hidden'>

            {/* User + role header */}
            <div className="shrink-0 pb-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-4">
                    <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                        style={{ backgroundColor: '#1e3a6e' }}
                    >
                        {initials}
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">
                            {staff?.otherNames} {staff?.surname}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{staffRoles(selectedRole)}</p>
                    </div>
                </div>

                {/* Role switcher dropdown — only when the user has multiple roles */}
                {path[1] !== "admin" && roleSlugs.length > 1 && (
                    <MobileRoleSwitcher
                        roleSlugs={roleSlugs}
                        selectedRole={selectedRole}
                        onSwitch={(role) => {
                            dispatch(setAdminRole(role));
                            dispatch(show({ type: "success", state: true, message: `Role switched to ${staffRoles(role)}` }));
                            closeMobileNav();
                            setTimeout(() => navigate("/"), 300);
                        }}
                    />
                )}
            </div>

            {/* Scrollable nav links */}
            <div className="flex-1 overflow-y-auto py-3">
                <ul className="space-y-0.5">
                    {(path[1] === "admin" ? navItems?.adminLinks : staffLinks)?.map((item, index) => {
                        const isActive = path[1] === "admin"
                            ? path[2] === item.active?.toLowerCase()
                            : path[1] === item.active?.toLowerCase();
                        return (
                            <li key={index}>
                                <Link
                                    to={item.href}
                                    onClick={closeMobileNav}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                        isActive
                                            ? 'bg-[#1e3a6e]/10 text-[#1e3a6e] dark:bg-blue-500/20 dark:text-blue-300'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                                >
                                    <HugeiconsIcon icon={item.icon} size={20} className="shrink-0" />
                                    <span>{item.name}</span>
                                </Link>
                            </li>
                        );
                    })}

                    {hasPermission('transactions:reverse_approve') && selectedRole !== "loan_officer" && path[1] !== "admin" && (
                        <li>
                            <Link
                                to="/reversals"
                                onClick={closeMobileNav}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    path[1] === "reversals"
                                        ? 'bg-[#1e3a6e]/10 text-[#1e3a6e] dark:bg-blue-500/20 dark:text-blue-300'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                            >
                                <HugeiconsIcon icon={navItems.reversalsIcon} size={20} className="shrink-0" />
                                <span>Reversals</span>
                            </Link>
                        </li>
                    )}
                </ul>
            </div>

            {/* Pinned footer: admin portal toggle + logout */}
            <div className="shrink-0 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-1">
                {hasPermission('admin:access') && (
                    <Link
                        to={path[1] === "admin" ? "/" : "/admin/home"}
                        onClick={closeMobileNav}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                        <HugeiconsIcon icon={ShieldUserIcon} size={20} className="shrink-0" />
                        <span>{path[1] === "admin" ? "Staff Portal" : "Admin Portal"}</span>
                    </Link>
                )}
                <button
                    onClick={logoutRequest}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                >
                    <HugeiconsIcon icon={Logout01Icon} size={20} className="shrink-0" />
                    <span>Sign out</span>
                </button>
            </div>
        </aside>
        </>
    );
}

export default Topbar;
